import { useState } from 'react'

import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Dropzone } from '@/components/ui/Dropzone'
import { ErrorState } from '@/components/ui/ErrorState'
import { Field, Input } from '@/components/ui/Input'
import { SectionHeader } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Spinner'
import { Status } from '@/components/ui/Status'
import { useImportFile, useImportSchema } from '@/hooks/useJobs'
import { cn } from '@/lib/cn'
import { formatBytes, formatNumber } from '@/lib/format'
import type { ImportPreview } from '@/types/api'

const ACCEPT = '.csv,.json,.xlsx,.xlsm,.tsv,text/csv,application/json'

/** Canonical field name -> the label a person recognises. */
const FIELD_LABELS: Record<string, string> = {
  judgment_number: 'Judgment #',
  docket_number: 'Docket #',
  date_entered: 'Date entered',
  debtor_names: 'Debtor name(s)',
  creditor_names: 'Creditor name(s)',
  amount: 'Amount',
  status: 'Status',
  case_title: 'Case title',
  filing_location: 'Filing location',
  matched_name: 'Matched name',
  matched_party_role: 'Matched party role',
  detail_fetched: 'Detail page read',
}

const FileIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4.5 w-4.5" fill="none" aria-hidden="true">
    <path
      d="M11.5 2.5H6A1.5 1.5 0 0 0 4.5 4v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5l-4-4z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M11.5 2.5v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
)

/**
 * Import judgments from a file the user chooses.
 *
 * The flow is deliberately three separate beats — choose, review what was
 * found, then import — because the import writes to a shared database that no
 * screen here can undo. The validation runs the server's own parser rather
 * than a second implementation in the browser, so what this reports is exactly
 * what the import will do.
 */
export function FileImportPanel({ onImported }: { onImported?: () => void }) {
  const [file, setFile] = useState<File | null>(null)
  const [label, setLabel] = useState('')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [showColumns, setShowColumns] = useState(false)

  const schema = useImportSchema()
  const importFile = useImportFile()

  const reset = () => {
    setFile(null)
    setPreview(null)
    setError(null)
    setLabel('')
  }

  const inspect = async (chosen: File) => {
    setFile(chosen)
    setPreview(null)
    setError(null)
    try {
      const result = await importFile.mutateAsync({ file: chosen, dryRun: true })
      setPreview(result.preview)
    } catch (cause) {
      setError(cause)
    }
  }

  const runImport = async () => {
    if (!file) return
    setError(null)
    try {
      await importFile.mutateAsync({ file, label: label.trim(), dryRun: false })
      reset()
      onImported?.()
    } catch (cause) {
      setError(cause)
    }
  }

  const busy = importFile.isPending

  return (
    <div className="flex flex-col gap-4">
      {file ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface-raised px-3 py-2.5 shadow-xs">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-surface-sunken text-ink-muted">
              <FileIcon />
            </span>
            <div className="min-w-0">
              <p className="truncate text-base font-medium text-ink">{file.name}</p>
              <p className="text-xs text-ink-muted">
                {formatBytes(file.size)}
                {busy ? ' · checking…' : preview ? ` · ${preview.detectedFormat.toUpperCase()}` : ''}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {busy ? <Spinner className="h-4 w-4" /> : null}
            <IconButton label="Remove file" variant="ghost" size="sm" onClick={reset} disabled={busy}>
              <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </IconButton>
          </div>
        </div>
      ) : (
        <Dropzone
          onFile={(chosen) => void inspect(chosen)}
          accept={ACCEPT}
          busy={busy}
          hint={
            <>
              CSV, JSON, TSV or Excel
              {schema.data ? ` · up to ${formatBytes(schema.data.maxBytes)}` : ''}
            </>
          }
        />
      )}

      {error ? (
        <ErrorState
          variant="inline"
          error={error}
          fallbackTitle="That file was not accepted"
          onRetry={file ? () => void inspect(file) : undefined}
        />
      ) : null}

      {preview ? <PreviewReport preview={preview} /> : null}

      {preview && preview.usableRows > 0 ? (
        <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface-raised p-3 shadow-xs">
          <Field label="Capture label" hint="Names this import in the audit trail.">
            <Input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder={file ? `${file.name.replace(/\.[^.]+$/, '')} (uploaded)` : ''}
            />
          </Field>
          <Button
            variant="primary"
            onClick={() => void runImport()}
            loading={busy}
            className="self-start"
          >
            Import {formatNumber(preview.usableRows)} record
            {preview.usableRows === 1 ? '' : 's'}
          </Button>
        </div>
      ) : null}

      {/* -- accepted columns --------------------------------------------- */}
      <div className="border-t border-line pt-3">
        <button
          type="button"
          onClick={() => setShowColumns((open) => !open)}
          aria-expanded={showColumns}
          className="inline-flex items-center gap-1.5 rounded-sm text-xs font-medium text-ink-secondary transition-colors hover:text-ink"
        >
          <svg
            viewBox="0 0 12 12"
            className={cn(
              'h-2.5 w-2.5 transition-transform duration-150 ease-out',
              showColumns && 'rotate-90',
            )}
            aria-hidden="true"
          >
            <path
              d="M4.5 3l3 3-3 3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Which columns are recognised?
        </button>

        {showColumns && schema.data ? (
          <div className="mt-3 flex flex-col gap-3">
            <p className="text-xs text-ink-secondary">
              Header spelling is forgiving: <code className="ident">Judgment #</code>,{' '}
              <code className="ident">judgment_number</code> and{' '}
              <code className="ident">judgmentNumber</code> are the same column. Every
              record needs a <strong className="text-ink">judgment or docket number</strong>{' '}
              so it can be identified and de-duplicated; everything else is optional.
            </p>
            <ul className="grid gap-1.5 sm:grid-cols-2">
              {Object.entries(schema.data.columns).map(([name, aliases]) => (
                <li
                  key={name}
                  className="flex flex-wrap items-baseline gap-1.5 rounded-md border border-line-subtle bg-surface-sunken px-2 py-1.5"
                >
                  <span className="text-sm font-medium text-ink">
                    {FIELD_LABELS[name] ?? name}
                  </span>
                  {schema.data!.identifyingColumns.includes(name) ? (
                    <Badge tone="accent">required</Badge>
                  ) : null}
                  <span className="ident text-xs text-ink-muted">
                    {aliases.slice(0, 3).join(', ')}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-ink-muted">
              Multiple names in one cell separate with{' '}
              <code className="ident">{schema.data.listSeparators.join('</code> or <code>')}</code>
              , never a comma, since names are stored “SURNAME, FORENAME”.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** What the server found, stated plainly before anything is stored. */
function PreviewReport({ preview }: { preview: ImportPreview }) {
  const mapped = Object.entries(preview.mappedColumns)
  const nothingUsable = preview.usableRows === 0
  const share = preview.totalRows > 0 ? preview.usableRows / preview.totalRows : 0

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface-sunken p-3">
      <SectionHeader
        level="eyebrow"
        title="What the file contains"
        actions={
          <Status
            meta={
              nothingUsable
                ? { label: 'Nothing usable', tone: 'critical' }
                : preview.skippedRows > 0
                  ? { label: `${formatNumber(preview.skippedRows)} skipped`, tone: 'warning' }
                  : { label: 'Ready to import', tone: 'good' }
            }
          />
        }
      />

      <div>
        <p className="text-base text-ink">
          <strong className="tabular text-lg font-semibold">
            {formatNumber(preview.usableRows)}
          </strong>{' '}
          of {formatNumber(preview.totalRows)} row
          {preview.totalRows === 1 ? '' : 's'} ready
        </p>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-chart-track">
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-300 ease-out',
              nothingUsable ? 'bg-critical' : 'bg-series-1',
            )}
            style={{ width: `${Math.round(share * 100)}%` }}
          />
        </div>
      </div>

      {mapped.length > 0 ? (
        <div>
          <p className="text-2xs font-semibold tracking-[0.06em] text-ink-muted uppercase">
            Columns matched
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1">
            {mapped.map(([header, field]) => (
              <li
                key={header}
                className="inline-flex items-center gap-1 rounded-md border border-line bg-surface-raised px-1.5 py-0.5 text-xs"
                title={`“${header}” maps to ${FIELD_LABELS[field] ?? field}`}
              >
                <span className="text-ink">{header}</span>
                <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 text-ink-faint" aria-hidden="true">
                  <path
                    d="M2 6h8M7 3l3 3-3 3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-ink-muted">{FIELD_LABELS[field] ?? field}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {preview.unrecognisedColumns.length > 0 ? (
        <p className="text-xs text-ink-secondary">
          <span className="font-medium text-warning">Ignored:</span>{' '}
          {preview.unrecognisedColumns.join(', ')}
        </p>
      ) : null}

      {preview.missingColumns.length > 0 ? (
        <p className="text-xs text-ink-muted">
          Not supplied:{' '}
          {preview.missingColumns.map((f) => FIELD_LABELS[f] ?? f).join(', ')}
        </p>
      ) : null}

      {preview.rowErrors.length > 0 ? (
        <details className="group text-xs">
          <summary className="w-fit cursor-pointer text-ink-secondary transition-colors hover:text-ink">
            <span className="group-open:hidden">Show </span>
            {formatNumber(preview.rowErrors.length)} row problem
            {preview.rowErrors.length === 1 ? '' : 's'}
          </summary>
          <ul className="scrollbar-slim mt-1.5 flex max-h-40 flex-col gap-0.5 overflow-y-auto text-ink-muted">
            {preview.rowErrors.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}
